<template>
  <div ref="campaignsTableContainer" class="grid-content">
    <b-table v-if="keywords" :data="keywords" hoverable sticky-header>
      <b-table-column
        v-for="column in columns"
        :key="column.id"
        :field="column.name"
        :cell-class="column.class" :label="column.label"
        :visible="isKeywordColumnVisible(column)"
        :width="column.width"
        :sortable="column.sortable"
        :header-class="'column-th-' + column.name"
      >
        <template v-slot="props">
          <div v-if="column.name === 'Keyword'" :style="getColumnStyle(column)">
            <a href="#" @click="emitToggleGoogleCampaignDetails($event)">{{ props.row[column.name] }}</a>
          </div>
          <div v-else :style="getColumnStyle(column)">
            {{ getFormattedValue(props.row[column.name], column.name) }}
          </div>
        </template>
      </b-table-column>

      <b-table-column
        v-slot="props"
        cell-class="cell-actions"
        field="actions"
        :label="$t('Campaigns.Grid.Columns.Actions')"
        width="265"
        :sticky="true"
        :visible="isActionsColumnVisible"
      >
        <div class="actions-list">
          <b-tooltip
            v-if="showFacebookAction(selectedCampaign.ChannelMetadataType)"
            :label="$t('Campaigns.AdGroupsGrid.ActionsColumn.GoToFacebook')"
            position="is-top"
            type="is-dark"
          >
            <a
              :href="`https://www.facebook.com/adsmanager/manage/adsets?${props.row.AdCampaignId}&selected_adset_ids=${props.row.AdGroupId}`"
              class="action action-alerts"
            >
              <icon-base width="8" height="16" view-box="0 0 264 512" icon-name="facebook"><icon-facebook /></icon-base>
            </a>
          </b-tooltip>
          <b-tooltip
            v-if="showGoogleAction(selectedCampaign.ChannelMetadataType)"
            :label="$t('Campaigns.AdGroupsGrid.ActionsColumn.GoToAdwords')"
            position="is-top"
            type="is-dark"
          >
            <a
              :href="`https://ads.google.com/aw/overview?campaignId=${props.row.AdCampaignId}&adGroupId=${props.row.AdGroupId}`"
              class="action action-alerts"
            >
              <icon-base width="16" height="16" view-box="0 0 16 16" icon-name="google"><icon-google /></icon-base>
            </a>
          </b-tooltip>
        </div>
      </b-table-column>
    </b-table>
  </div>
</template>

<script lang="ts">
import moment from "moment";
import { LoaderComponent } from "vue-loading-overlay";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import i18n from "@/i18n";
import { campaignEditRoute } from "@/router";
import { DataSourceResult } from "@/models/DataSourceResult";
import { FilterCollection } from "@/models/FilterCollection";
import { GridColumn } from "@/models/GridColumn";
import { CampaignKeywordGridViewModel } from "@/models/campaigns/CampaignKeywordGridViewModel";
import { CampaignsPageViewModel } from "@/models/campaigns/Campaign";
import { CampaignGridView, RefreshGridMode } from "@/models/campaigns/CampaignGridView";
import { CampaignStatus } from "@/models/campaigns/CampaignStatus";
import { ChannelMetadataType } from "@/models/campaigns/ChannelMetadataType";
import CampaignService from "@/services/CampaignService";
import HelperService from "@/services/HelperService";
import CampaignsKeywordsGridHeadline from "./CampaignsKeywordsGridHeadline.vue";
import IconBase from "../../components/IconBase.vue";
import IconBell from "../../components/icons/IconBell.vue";
import IconChartLine from "../../components/icons/IconChartLine.vue";
import IconChartPie from "../../components/icons/IconChartPie.vue";
import IconEye from "../../components/icons/IconEye.vue";
import IconFacebook from "../../components/icons/IconFacebook.vue";
import IconGoogle from "../../components/icons/IconGoogle.vue";
import IconHistory from "../../components/icons/IconHistory.vue";
import IconMobileNo from "../../components/icons/IconMobileNo.vue";
import IconMobileYes from "../../components/icons/IconMobileYes.vue";
import IconPencil from "../../components/icons/IconPencil.vue";
import IconTrash from "../../components/icons/IconTrash.vue";
import IconUpdate from "../../components/icons/IconUpdate.vue";

const namespace = "campaignsModule";

@Component({
  components: {
    CampaignsKeywordsGridHeadline,
    IconBase,
    IconChartLine,
    IconChartPie,
    IconEye,
    IconFacebook,
    IconGoogle,
    IconPencil,
    IconBell,
    IconHistory,
    IconMobileYes,
    IconMobileNo,
    IconTrash,
    IconUpdate,
  },
})
export default class CampaignsKeywordsGridContent extends Vue {
  @Prop() readonly keywords!: CampaignKeywordGridViewModel[];
  @Prop() readonly columns!: GridColumn[];

  //#region Vuex getters and actions

  @Getter("campaigns", { namespace }) campaigns!: DataSourceResult;
  @Getter("campaignsLoading", { namespace }) campaignsLoading!: boolean;
  @Getter("campaignsPageData", { namespace }) campaignsPageData!: CampaignsPageViewModel;
  @Getter("columnsFilters", { namespace }) columnsFilters!: FilterCollection;
  @Getter("datePeriod", { namespace }) datePeriod!: Date[];
  @Getter("page", { namespace }) page!: number;
  @Getter("selectedCampaign", { namespace }) selectedCampaign!: CampaignGridView;
  @Getter("uniqueByCampaign", { namespace }) uniqueByCampaign!: boolean;
  @Action("setNeedToRefreshCampaignsGrid", { namespace }) setNeedToRefreshCampaignsGrid!: (
    needToRefreshCampaignsGrid: RefreshGridMode
  ) => void;
  @Action("setColumnsFilters", { namespace }) setColumnsFilters!: (columnsFilters: FilterCollection) => void;
  @Action("setGraphsHiddenOnScroll", { namespace }) setGraphsHiddenOnScroll!: (graphsHiddenOnScroll: boolean) => void;
  @Action("setPage", { namespace }) setPage!: (page: number) => void;
  @Action("setSelectedCampaign", { namespace }) setSelectedCampaign!: (selectedCampaign?: CampaignGridView) => void;
  @Action("setSelectedCampaignId", { namespace }) setSelectedCampaignId!: (campaignClientId?: number) => void;
  @Action("setSortDir", { namespace }) setSortDir!: (sortDir: string) => void;
  @Action("setSortField", { namespace }) setSortField!: (sortField: string) => void;

  //#endregion Vuex getters and actions
  

  //#region Properties

  isColumnFiltersUpdated = false;
  tableDataKey = 0;
  campaignsTableContainer: Element | Window | null = null;
  campaignsTableLoader?: LoaderComponent = undefined;

  CampaignStatus = CampaignStatus;
  
  //#endregion Properties


  //#region Watchers

  @Watch("campaignsLoading", { immediate: true })
  onCampaignsLoadingChange(newValue: boolean, oldValue: boolean): void {
    if (newValue) {
      this.campaignsTableLoader = this.$loading.show({
        container: this.$refs.campaignsTableContainer
      });
    } else {
      this.campaignsTableLoader?.hide();
      if (this.$el) {
        if (window.innerWidth > 768) {
          this.campaignsTableContainer = this.$el.querySelector(".campaigns-table .table-wrapper");
          if (this.campaignsTableContainer) {
            if (this.page === 1) {
              (this.campaignsTableContainer as Element).scrollTop = 0;
            }
            this.campaignsTableContainer.addEventListener('scroll', this.onScrollTable);
          }
        } else {
          this.campaignsTableContainer = window;
          if (this.page === 1) {
            window.scrollTo({ top: 0 });
          }
          this.campaignsTableContainer.addEventListener('scroll', this.onScrollWindow);
        }
      }
    }
  }

  //#endregion Watchers


  //#region Getters

  get campaignEditRouteValue(): string {
    return campaignEditRoute;
  }

  get isActionsColumnVisible(): boolean {
    const column: GridColumn | undefined = this.columns.find((i: GridColumn) => i.name === "Actions");
    if (column) {
      return column.visible;
    } else {
      return false;
    }
  }

  //#endregion Getters


  //#region Handlers

  onPageChange(page: number) {
    this.setPage(page);
    this.setNeedToRefreshCampaignsGrid(RefreshGridMode.GridOnly);
  }

  onPageSort(fieldName: string, sortDirection: string, e: PointerEvent) {
    this.setSortDir(sortDirection);
    this.setSortField(fieldName);
    this.setPage(1);
    this.setNeedToRefreshCampaignsGrid(RefreshGridMode.GridOnly);
  }

  onScrollWindow(e: Event) {
    const target: Document = e.target as Document;
    if (this.campaigns.Total && target.documentElement.scrollTop >= document.documentElement.offsetHeight - window.innerHeight
          && this.page * CampaignService.getPageSize() < this.campaigns.Total) {
      if (this.campaignsTableContainer) {
        this.campaignsTableContainer.removeEventListener('scroll', this.onScrollWindow);
      };
      this.onPageChange(this.page + 1);
    }
  }

  onScrollTable(e: Event): void {
    const target: HTMLDivElement = e.target as HTMLDivElement;
    const EXTRA_PIXELS = 2; // for browser zoom
    const contentHeight: number = target.scrollHeight - target.clientHeight - EXTRA_PIXELS;
    if (
      this.campaigns.Total &&
      target.scrollTop >= contentHeight &&
      this.page * CampaignService.getPageSize() < this.campaigns.Total
    ) {
      if (this.campaignsTableContainer) {
        this.campaignsTableContainer.removeEventListener("scroll", this.onScrollTable);
      }
      this.onPageChange(this.page + 1);
    }
    this.setGraphsHiddenOnScroll(target.scrollTop > 10 ? true : false);
  }

  emitToggleGoogleCampaignDetails(e: Event): void {
    e.preventDefault();
    this.$emit("toggle-google-campaign-details");
  }

  //#endregion Handlers


  //#region Private methods

  getColumnStyle(column: GridColumn): string {
    return HelperService.getColumnStyle(column);
  }

  getFormattedValue(value: number | string | Date, fieldName: string): string {
    if (!value) {
      return "";
    }
    if (fieldName === "StartDate" || fieldName === "EndDate") {
      return moment(value as Date).format("DD/MM/yyyy HH:mm");
    } else if (fieldName === "Price") {
      return i18n.locale === "he-IL" ? `₪${Math.ceil(value as number)}` : `${Math.ceil(value as number)}₪`;
    } else {
      return value as string;
    }
  }

  isKeywordColumnVisible(column: GridColumn): boolean {
    const columnsForPricesAllowed: string[] = ["DealAmount", "LeadPrice", "MeetingPrice", "ROI", "Spent"];
    return (
      column.visible &&
      !column.forbidden &&
      column.id !== 1 &&
      (this.campaignsPageData.PricesAllowed ||
        (!this.campaignsPageData.PricesAllowed && !columnsForPricesAllowed.includes(column.name)))
    );
  }

  showFacebookAction(metadataType: ChannelMetadataType): boolean {
    return (
      [
        ChannelMetadataType.FacebookAds,
        ChannelMetadataType.InstagramAds,
        ChannelMetadataType.FacebookAdLeadCampaign,
        ChannelMetadataType.InstagramAdLeadCampaign,
      ].indexOf(metadataType) >= 0
    );
  }

  showGoogleAction(metadataType: ChannelMetadataType): boolean {
    return [ChannelMetadataType.GoogleAdwords].indexOf(metadataType) >= 0;
  }

  //#endregion Private methods
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.grid-content {
  .table thead th,
  .table tbody td {
    &.cell-keyword {
      font-weight: 700;
    }

    &.cell-channel-metadata-type-string,
    &.cell-title {
      & > div {
        align-items: center;
        display: flex;

        .icon-w {
          display: flex;
          margin-inline-end: 0.5rem;
          max-width: 1rem;
        }
      }
    }

    &.cell-title {
      & > div {
        .text.active {
          color: $primary;
          cursor: pointer;
        }
      }
    }

    .campaign-status {
      align-items: center;
      display: flex;

      .campaign-name-status {
        display: flex;
        align-items: center;
        font-size: 14px;
        @media (max-width: 768px) {
          justify-content: flex-end;
        }
        .indicator {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          border: 1.5px solid #aaa;
          @media (max-width: 768px) {
            order: 2;
            margin: 0 5px 0 0;
          }
          &.indicator-1 {
            background-color: $success;
          }
          &.indicator-2 {
            border-color: $success;
          }
          &.indicator-3 {
            border-color: $danger;
          }
          &.indicator-4 {
            background: $lightd;
            border-color: $lightd;
          }
          &.indicator-5 {
            background: $danger;
            border-color: $danger;
          }
        }
      }

      .status-str-text {
        font-size: 0.9rem;
        margin-inline-start: 0.3rem;

        &.clickable {
          color: $danger;
          cursor: pointer;
        }
      }
    }
  }

  .actions-list {
    .action {
      &.action-alerts {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        .icon-w {
          line-height: 0;
        }
        .tag.is-small {
          position: absolute;
          left: 50%;
          margin-left: 1px;
          top: -11px;
          height: 19px;
          line-height: 12px;
          border: 2px solid #fff;
          border-radius: 4px;
          padding: 0;
          width: auto;
          .tag-i {
            border: 1px solid $danger;
            background: #fff;
            color: $danger;
            border-radius: 2px;
            padding: 0 2px;
            font-weight: 600;
          }
        }
      }

      &.action-phone {
        .icon-mobile-yes {
          color: $success;
        }
        .icon-mobile-no {
          color: $danger;
        }
      }
    }

    .tooltip-phone {
      .tooltip-content {
        transform: translateX(-20%);
        &:before {
          left: 20%;
        }
      }
    }
  }
}

.grid-summary {
  padding: 8px 23px 9px;
  border-top: 1px solid #d3d6e2;

  .grid-summary-inner {
    .summary-label {
      font-weight: 600;
    }
  }
}

html[dir="ltr"] {
  .grid-content {
    .actions-list {
      .tooltip-phone {
        .tooltip-content {
          transform: translateX(-80%);
          &:before {
            left: 80%;
          }
        }
      }
    }
  }
}
</style>
